import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { SyncStatusBadge, type SyncStatus } from '../components/SyncStatusBadge.js';

interface ExportConfig {
  id: string;
  name: string;
  dataSource: string;
  scheduleType: string;
  lastSyncStatus: SyncStatus | null;
  lastSyncAt: string | null;
  lastSyncRows: number | null;
  accountId: string;
  isEnabled: boolean;
  createdAt: string;
}

export function Exports() {
  const [exports_, setExports] = useState<ExportConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState<Set<string>>(new Set());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadExports = useCallback(async () => {
    try {
      setError('');
      const exportsRes = await api.get<{ exports: ExportConfig[]; total: number }>('/exports');
      setExports(exportsRes.exports);
      return exportsRes.exports;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load exports');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExports();
  }, [loadExports]);

  // Poll while any export is syncing — check every 5s, stop when all done
  useEffect(() => {
    if (syncing.size === 0) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    // Start polling
    pollRef.current = setInterval(async () => {
      const updated = await loadExports();
      if (!updated) return;

      // Check if any syncing export has finished
      setSyncing((prev) => {
        const next = new Set(prev);
        for (const id of prev) {
          const exp = updated.find((e) => e.id === id);
          if (exp && exp.lastSyncStatus !== null && exp.lastSyncStatus !== 'running') {
            next.delete(id);
          }
        }
        return next;
      });
    }, 5000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [syncing.size, loadExports]);

  async function handleSync(id: string) {
    setSyncing((prev) => new Set(prev).add(id));
    setError('');
    try {
      await api.post(`/exports/${id}/sync`);
      // Mark as running immediately in UI
      setExports((prev) =>
        prev.map((e) => (e.id === id ? { ...e, lastSyncStatus: 'running' as SyncStatus } : e)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
      setSyncing((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  async function handleDelete(exp: ExportConfig) {
    if (!confirm(`Delete export "${exp.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/exports/${exp.id}`);
      setExports((prev) => prev.filter((e) => e.id !== exp.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete export');
    }
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Data Exports</h2>
          <p className="text-sm text-gray-500">Configure and manage your data sync pipelines.</p>
        </div>
        <Link
          to="/exports/new"
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          New Export
        </Link>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {exports_.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <ExportIcon />
          <p className="mt-2 text-sm font-medium text-gray-900">No exports yet</p>
          <p className="mt-1 text-xs text-gray-500">
            Create your first data export to start syncing Amazon data to Google Sheets.
          </p>
          <Link
            to="/exports/new"
            className="mt-4 inline-block rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Create Export
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {exports_.map((exp) => {
            const isSyncing = syncing.has(exp.id) || exp.lastSyncStatus === 'running';
            return (
              <div
                key={exp.id}
                className={`rounded-xl border bg-white p-4 transition-colors ${
                  isSyncing ? 'border-brand-300 bg-brand-50/30' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">{exp.name}</p>
                      {isSyncing ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-700">
                          <Spinner />
                          Syncing...
                        </span>
                      ) : (
                        exp.lastSyncStatus && <SyncStatusBadge status={exp.lastSyncStatus} />
                      )}
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                        {exp.scheduleType}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Source: {exp.dataSource}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {isSyncing
                        ? 'Sync in progress...'
                        : exp.lastSyncAt
                          ? `Last sync: ${new Date(exp.lastSyncAt).toLocaleString()}${
                              exp.lastSyncRows != null ? ` (${exp.lastSyncRows.toLocaleString()} rows)` : ''
                            }`
                          : 'Never synced'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleSync(exp.id)}
                      disabled={isSyncing}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                        isSyncing
                          ? 'border-brand-200 text-brand-500 cursor-not-allowed'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {isSyncing ? (
                        <span className="flex items-center gap-1.5">
                          <Spinner />
                          Syncing
                        </span>
                      ) : (
                        'Sync Now'
                      )}
                    </button>
                    <Link
                      to={`/exports/${exp.id}/edit`}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(exp)}
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg className="mx-auto h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
    </svg>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-32 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-64 rounded bg-gray-200" />
        </div>
        <div className="h-9 w-28 rounded-lg bg-gray-200" />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-24 rounded-xl bg-gray-200" />
      ))}
    </div>
  );
}
